import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ApiError, apiRequest } from "../lib/api";

export interface AuthUser {
  tenantId: string;
  tenantGuid: string;
  accountId: string;
  username: string;
  roleCode: string;
  permissions: string[];
}

interface AuthSession {
  token: string;
  refreshToken: string;
  expiresAt: number;
  refreshExpiresAt: number;
  user: AuthUser;
}

interface LoginInput {
  username: string;
  password: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  status: "loading" | "authenticated" | "anonymous";
  login(input: LoginInput): Promise<void>;
  logout(): Promise<void>;
  request<T>(path: string, init?: RequestInit): Promise<T>;
  hasPermission(permission: string): boolean;
}

const SESSION_KEY = "xmirror.admin.session";
const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredSession(): AuthSession | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AuthSession;
    return parsed.token && parsed.refreshToken && parsed.user ? parsed : null;
  } catch {
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  }
}

function storeSession(session: AuthSession | null) {
  if (!session) {
    sessionStorage.removeItem(SESSION_KEY);
    return;
  }
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<AuthSession | null>(() =>
    readStoredSession(),
  );
  const [status, setStatus] =
    useState<AuthContextValue["status"]>("loading");

  const commitSession = useCallback((next: AuthSession | null) => {
    storeSession(next);
    setSession(next);
    setStatus(next ? "authenticated" : "anonymous");
  }, []);

  const refresh = useCallback(
    async (current: AuthSession): Promise<AuthSession> => {
      const next = await apiRequest<AuthSession>("/api/auth/refresh", {
        method: "POST",
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
      commitSession(next);
      return next;
    },
    [commitSession],
  );

  const request = useCallback(
    async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
      if (!session) {
        throw new ApiError(401, { message: "authentication_required" });
      }
      try {
        return await apiRequest<T>(path, init, session.token);
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 401) {
          throw error;
        }
        try {
          const next = await refresh(session);
          return await apiRequest<T>(path, init, next.token);
        } catch (refreshError) {
          commitSession(null);
          throw refreshError;
        }
      }
    },
    [commitSession, refresh, session],
  );

  useEffect(() => {
    let active = true;
    async function hydrate() {
      if (!session) {
        if (active) setStatus("anonymous");
        return;
      }
      try {
        const user = await apiRequest<AuthUser>(
          "/api/auth/me",
          {},
          session.token,
        );
        if (active) {
          commitSession({ ...session, user });
        }
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 401) {
          if (active) setStatus("authenticated");
          return;
        }
        try {
          await refresh(session);
        } catch {
          if (active) commitSession(null);
        }
      }
    }
    void hydrate();
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(
    async (input: LoginInput) => {
      const next = await apiRequest<AuthSession>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(input),
      });
      commitSession(next);
    },
    [commitSession],
  );

  const logout = useCallback(async () => {
    if (session) {
      try {
        await apiRequest("/api/auth/logout", { method: "POST" }, session.token);
      } finally {
        commitSession(null);
      }
    } else {
      commitSession(null);
    }
  }, [commitSession, session]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user || null,
      status,
      login,
      logout,
      request,
      hasPermission: (permission) =>
        Boolean(session?.user.permissions.includes(permission)),
    }),
    [login, logout, request, session, status],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("AuthProvider is missing");
  return context;
}
